import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useAidPreferenceClessDeleteMutation } from "src/app/features/EmvKernelProfile/subFeatures/AidPreferenceCless/services/server/AidPreferenceClessApi";
import type { AidPreferenceClessEntity } from "src/app/features/EmvKernelProfile/subFeatures/AidPreferenceCless/types/AidPreferenceClessEntity";
import type { ExtendedColumnsType } from "../../../../../../../components";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import { Actions } from "../../../../../../../components/Table/types/types";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import {
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../../../core/setup/redux/state/notifications/notificationsState";

const getColumnsConfig = (t: Function): ExtendedColumnsType<AidPreferenceClessEntity>[] => {
    return [
        {
            title: t("emvKernelConfiguration.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("emvKernelConfigurationCreateForm.aidPrimary"),
            key: "aidPrimary",
            dataIndex: "aidPrimary",
        },
        {
            title: t("emvKernelConfigurationCreateForm.appSelectionIndicatorAidShort"),
            key: "appSelectionIndicatorAid",
            dataIndex: "appSelectionIndicatorAid",
        },
        {
            title: t("emvKernelConfigurationCreateForm.aidsSecondary"),
            key: "aidsSecondary",
            dataIndex: "aidsSecondary",
        },
        {
            title: t("emvKernelConfigurationCreateForm.sequence"),
            key: "sequence",
            dataIndex: "sequence",
        },
    ];
};

export interface AidPreferenceClessTableProps extends AntCustomFormField<AidPreferenceClessEntity[]>, CommonFieldAttributes {}

export const AidPreferencesClessTable: FC<AidPreferenceClessTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const resourceUrl = `${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${id}/${ResourceName.AID_PREFERENCES_CLESS}`;

    const onDataChanged = (data: AidPreferenceClessEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: AidPreferenceClessEntity) => navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${record.id}`);

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useAidPreferenceClessDeleteMutation();

    const deleteCallback = (item: AidPreferenceClessEntity) => {
        deleteTableItem({
            emvKernelProfileId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: AidPreferenceClessEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: AidPreferenceClessEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.id,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<AidPreferenceClessEntity>
            getColumnsConfig={getColumnsConfig}
            name="aidPreferencescless_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: AidPreferenceClessEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.ADD, Actions.COPY]}
            actionCallbacks={{ add: addCallback, delete: (item?: AidPreferenceClessEntity) => item && deleteCallback(item) }}
            addButtonName="aidPreferencesClessTable_addButton"
        />
    );
};
